import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  FileText, User, Home, MapPin, PoundSterling, Calendar, 
  Check, ArrowRight, ArrowLeft, Download, Send, Bed, Shield,
  PenTool, Mail, ExternalLink, Loader2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TenancyAgreement = () => {
  const navigate = useNavigate();
  const { isArabic } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [signingLink, setSigningLink] = useState(null);
  const [sendingForSignature, setSendingForSignature] = useState(false);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    // Landlord
    landlord_name: '',
    landlord_address: '',
    landlord_email: '',
    landlord_phone: '',
    
    // Tenant
    tenant_name: '',
    tenant_address: '',
    tenant_email: '',
    tenant_phone: '',
    tenant_id_type: 'passport',
    tenant_id_number: '',
    
    // Property
    property_address: '',
    property_postcode: '',
    property_type: 'flat',
    bedrooms: '2',
    furnishing: 'unfurnished',
    
    // Financial
    monthly_rent: '',
    deposit_amount: '',
    deposit_scheme: 'DPS',
    
    // Tenancy
    tenancy_start_date: '',
    tenancy_end_date: '',
    tenancy_type: 'Assured Shorthold Tenancy',
    max_occupants: 4,
    pets_allowed: false,
    additional_terms: '',
    
    // Utility Payments
    tenant_pays_electricity: true,
    tenant_pays_gas: true,
    tenant_pays_water: true,
    tenant_pays_council_tax: true
  });

  useEffect(() => {
    // Check if admin is logged in
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const propertyTypes = [
    { id: 'detached', label: 'Detached House' },
    { id: 'semi-detached', label: 'Semi-Detached' },
    { id: 'terraced', label: 'Terraced' },
    { id: 'flat', label: 'Flat / Apartment' },
    { id: 'studio', label: 'Studio' },
  ];

  const depositSchemes = [
    { id: 'DPS', label: 'Deposit Protection Service (DPS)' },
    { id: 'TDS', label: 'Tenancy Deposit Scheme (TDS)' },
    { id: 'MyDeposits', label: 'MyDeposits' },
  ];

  const idTypes = [
    { id: 'passport', label: isArabic ? 'جواز سفر' : 'Passport' },
    { id: 'driving_licence', label: isArabic ? 'رخصة قيادة' : 'Driving Licence' },
    { id: 'national_id', label: isArabic ? 'بطاقة وطنية' : 'National ID Card' },
    { id: 'brp', label: 'BRP (Biometric Residence Permit)' },
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-agreement/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          monthly_rent: parseFloat(formData.monthly_rent) || 0,
          deposit_amount: parseFloat(formData.deposit_amount) || 0,
          bedrooms: parseInt(formData.bedrooms) || 0,
          max_occupants: parseInt(formData.max_occupants) || 4
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPdfData(data);
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        console.error('Error generating agreement:', errorData);
        alert(errorData.detail || 'Failed to generate agreement. Please try again.');
      }
    } catch (error) {
      console.error('Error generating agreement:', error);
      alert('Failed to connect to server. Please try again.');
    }
    setIsSubmitting(false);
  };

  const downloadPDF = () => {
    if (pdfData?.pdf_base64) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdfData.pdf_base64}`;
      link.download = pdfData.filename || 'Tenancy_Agreement.pdf';
      link.click();
    }
  };

  const sendForESignature = async () => {
    if (!formData.tenant_email) {
      toast({ title: "Error", description: "Tenant email is required for e-signature", variant: "destructive" });
      return;
    }

    setSendingForSignature(true);
    try {
      // First create/update the tenant
      const tenantResponse = await fetch(`${API_URL}/api/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.tenant_name,
          email: formData.tenant_email,
          phone: formData.tenant_phone,
          property_address: formData.property_address,
          monthly_rent: parseFloat(formData.monthly_rent) || 0,
          deposit_amount: parseFloat(formData.deposit_amount) || 0,
          tenancy_start: formData.tenancy_start_date,
          tenancy_end: formData.tenancy_end_date,
          landlord_name: formData.landlord_name,
          landlord_email: formData.landlord_email,
          landlord_phone: formData.landlord_phone
        })
      });

      if (!tenantResponse.ok) {
        throw new Error('Failed to create tenant record');
      }

      const tenantData = await tenantResponse.json();
      const tenantId = tenantData.tenant_id || tenantData.id;

      // Now send for e-signature using the alternative endpoint
      const signatureResponse = await fetch(`${API_URL}/api/send-signature-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId })
      });

      if (signatureResponse.ok) {
        const sigData = await signatureResponse.json();
        setSigningLink(sigData.signing_link);
        toast({ 
          title: "E-Signature Request Sent!", 
          description: `Signing link sent to ${formData.tenant_email}` 
        });
      } else {
        const error = await signatureResponse.json();
        toast({ title: "Error", description: error.detail || "Failed to send signature request", variant: "destructive" });
      }
    } catch (error) {
      console.error('E-signature error:', error);
      toast({ title: "Error", description: "Failed to send e-signature request", variant: "destructive" });
    }
    setSendingForSignature(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#404040] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-12 h-1 mx-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (isSubmitted && pdfData) {
    const emailSent = pdfData.email_sent !== false;
    
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="border-none shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#404040] mb-4">
                Agreement Generated!
              </h2>
              {emailSent ? (
                <>
                  <p className="text-lg text-gray-600 mb-2">
                    The tenancy agreement has been created and sent to
                  </p>
                  <p className="text-[#FF8C42] font-semibold mb-4">
                    info@harryandjacobs.com
                  </p>
                </>
              ) : (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-700 font-medium mb-1">Email not sent</p>
                  <p className="text-sm text-yellow-600">
                    Brevo API key not configured. Please download the PDF manually.
                  </p>
                </div>
              )}
              
              {/* E-Signature Section */}
              <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-200">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <PenTool className="w-6 h-6 text-amber-600" />
                  <h3 className="text-lg font-bold text-amber-800">Send for E-Signature</h3>
                </div>
                <p className="text-sm text-amber-700 mb-4">
                  Send this agreement to <strong>{formData.tenant_email}</strong> for digital signature
                </p>
                
                {signingLink ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <Check className="w-5 h-5" />
                      <span className="font-semibold">Signing link sent!</span>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-gray-500 mb-1">Signing Link (expires in 7 days):</p>
                      <div className="flex items-center gap-2">
                        <input 
                          type="text" 
                          value={signingLink} 
                          readOnly 
                          className="flex-1 text-xs p-2 bg-gray-50 rounded border"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(signingLink);
                            toast({ title: "Copied!", description: "Signing link copied to clipboard" });
                          }}
                        >
                          Copy
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => window.open(signingLink, '_blank')}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={sendForESignature}
                    disabled={sendingForSignature || !formData.tenant_email}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-6"
                  >
                    {sendingForSignature ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Send E-Signature Request
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={downloadPDF}
                  className="bg-[#404040] hover:bg-[#333] text-white"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  onClick={() => {
                    setIsSubmitted(false);
                    setCurrentStep(1);
                    setSigningLink(null);
                    setFormData({
                      landlord_name: '',
                      landlord_address: '',
                      landlord_email: '',
                      landlord_phone: '',
                      tenant_name: '',
                      tenant_address: '',
                      tenant_email: '',
                      tenant_phone: '',
                      tenant_id_type: 'passport',
                      tenant_id_number: '',
                      property_address: '',
                      property_postcode: '',
                      property_type: 'flat',
                      bedrooms: '2',
                      furnishing: 'unfurnished',
                      monthly_rent: '',
                      deposit_amount: '',
                      deposit_scheme: 'DPS',
                      tenancy_start_date: '',
                      tenancy_end_date: '',
                      tenancy_type: 'Assured Shorthold Tenancy',
                      max_occupants: 4,
                      pets_allowed: false,
                      additional_terms: '',
                      tenant_pays_electricity: true,
                      tenant_pays_gas: true,
                      tenant_pays_water: true,
                      tenant_pays_council_tax: true
                    });
                  }}
                  variant="outline"
                >
                  Create Another Agreement
                </Button>
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg text-left">
                <h3 className="font-semibold text-[#404040] mb-2">What happens next:</h3>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                  <li><strong>Tenant receives email</strong> with a secure signing link</li>
                  <li><strong>Tenant signs digitally</strong> using draw or type signature</li>
                  <li><strong>You get notified</strong> when the agreement is signed</li>
                  <li><strong>Both parties receive</strong> the signed PDF via email</li>
                  <li>Register deposit with {formData.deposit_scheme} within 30 days</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#404040] rounded-full mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#404040]">
            Create Tenancy Agreement
          </h1>
          <p className="text-gray-600 mt-2">
            UK Assured Shorthold Tenancy (AST) Agreement
          </p>
        </div>

        {renderStepIndicator()}

        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {/* Step 1: Landlord & Tenant Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">Landlord & Tenant Details</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Landlord */}
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-[#FF8C42] flex items-center gap-2">
                      <User className="w-5 h-5" /> Landlord
                    </h3>
                    <div>
                      <Label htmlFor="landlord_name">Full Name</Label>
                      <Input
                        id="landlord_name"
                        value={formData.landlord_name}
                        onChange={(e) => setFormData({...formData, landlord_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="landlord_address">Address</Label>
                      <Input
                        id="landlord_address"
                        value={formData.landlord_address}
                        onChange={(e) => setFormData({...formData, landlord_address: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="landlord_email">Email</Label>
                      <Input
                        id="landlord_email"
                        type="email"
                        value={formData.landlord_email}
                        onChange={(e) => setFormData({...formData, landlord_email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="landlord_phone">Phone</Label>
                      <Input
                        id="landlord_phone"
                        type="tel"
                        value={formData.landlord_phone}
                        onChange={(e) => setFormData({...formData, landlord_phone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Tenant */}
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-[#404040] flex items-center gap-2">
                      <User className="w-5 h-5" /> Tenant
                    </h3>
                    <div>
                      <Label htmlFor="tenant_name">Full Name</Label>
                      <Input
                        id="tenant_name"
                        value={formData.tenant_name}
                        onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tenant_address">Current Address</Label>
                      <Input
                        id="tenant_address"
                        value={formData.tenant_address}
                        onChange={(e) => setFormData({...formData, tenant_address: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tenant_email">Email</Label>
                      <Input
                        id="tenant_email"
                        type="email"
                        value={formData.tenant_email}
                        onChange={(e) => setFormData({...formData, tenant_email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tenant_phone">Phone</Label>
                      <Input
                        id="tenant_phone"
                        type="tel"
                        value={formData.tenant_phone}
                        onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Tenant ID */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tenant ID Type</Label>
                    <select
                      value={formData.tenant_id_type}
                      onChange={(e) => setFormData({...formData, tenant_id_type: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      {idTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="tenant_id_number">ID Number</Label>
                    <Input
                      id="tenant_id_number"
                      value={formData.tenant_id_number}
                      onChange={(e) => setFormData({...formData, tenant_id_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Property Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">Property Details</h2>
                </div>

                <div>
                  <Label htmlFor="property_address">Property Address</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="property_address"
                      value={formData.property_address}
                      onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                      className="pl-10"
                      placeholder="Full address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="property_postcode">Postcode</Label>
                    <Input
                      id="property_postcode"
                      value={formData.property_postcode}
                      onChange={(e) => setFormData({...formData, property_postcode: e.target.value})}
                      className="mt-1"
                      placeholder="e.g., HA3 5AH"
                    />
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <select
                      value={formData.property_type}
                      onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      {propertyTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Bed className="w-4 h-4" /> Bedrooms
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Furnishing</Label>
                    <select
                      value={formData.furnishing}
                      onChange={(e) => setFormData({...formData, furnishing: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      <option value="unfurnished">Unfurnished</option>
                      <option value="part_furnished">Part Furnished</option>
                      <option value="furnished">Fully Furnished</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max Occupants</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.max_occupants}
                      onChange={(e) => setFormData({...formData, max_occupants: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center mt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.pets_allowed}
                        onChange={(e) => setFormData({...formData, pets_allowed: e.target.checked})}
                        className="w-5 h-5"
                      />
                      <span>Pets Allowed</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Financial Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">Financial Details</h2>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <PoundSterling className="w-4 h-4" /> Monthly Rent (£)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.monthly_rent}
                      onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})}
                      className="mt-1"
                      placeholder="e.g., 1500"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Deposit Amount (£)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.deposit_amount}
                      onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                      className="mt-1"
                      placeholder="Usually 5 weeks rent"
                    />
                  </div>
                </div>

                <div>
                  <Label>Deposit Protection Scheme</Label>
                  <select
                    value={formData.deposit_scheme}
                    onChange={(e) => setFormData({...formData, deposit_scheme: e.target.value})}
                    className="w-full mt-1 p-2 border rounded-md"
                  >
                    {depositSchemes.map((scheme) => (
                      <option key={scheme.id} value={scheme.id}>{scheme.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Tenancy Start Date
                    </Label>
                    <Input
                      type="date"
                      value={formData.tenancy_start_date}
                      onChange={(e) => setFormData({...formData, tenancy_start_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Tenancy End Date
                    </Label>
                    <Input
                      type="date"
                      value={formData.tenancy_end_date}
                      onChange={(e) => setFormData({...formData, tenancy_end_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Utility Payments Section */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-[#404040] mb-4">Utility & Bill Responsibility</h3>
                  <p className="text-sm text-gray-600 mb-4">Select who is responsible for each utility payment:</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                      <span className="text-sm font-medium">Electricity</span>
                      <div className="flex gap-2">
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${formData.tenant_pays_electricity ? 'bg-[#FF8C42] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="electricity"
                            checked={formData.tenant_pays_electricity}
                            onChange={() => setFormData({...formData, tenant_pays_electricity: true})}
                            className="hidden"
                          />
                          Tenant
                        </label>
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${!formData.tenant_pays_electricity ? 'bg-[#404040] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="electricity"
                            checked={!formData.tenant_pays_electricity}
                            onChange={() => setFormData({...formData, tenant_pays_electricity: false})}
                            className="hidden"
                          />
                          Landlord
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                      <span className="text-sm font-medium">Gas</span>
                      <div className="flex gap-2">
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${formData.tenant_pays_gas ? 'bg-[#FF8C42] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="gas"
                            checked={formData.tenant_pays_gas}
                            onChange={() => setFormData({...formData, tenant_pays_gas: true})}
                            className="hidden"
                          />
                          Tenant
                        </label>
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${!formData.tenant_pays_gas ? 'bg-[#404040] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="gas"
                            checked={!formData.tenant_pays_gas}
                            onChange={() => setFormData({...formData, tenant_pays_gas: false})}
                            className="hidden"
                          />
                          Landlord
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                      <span className="text-sm font-medium">Water & Sewerage</span>
                      <div className="flex gap-2">
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${formData.tenant_pays_water ? 'bg-[#FF8C42] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="water"
                            checked={formData.tenant_pays_water}
                            onChange={() => setFormData({...formData, tenant_pays_water: true})}
                            className="hidden"
                          />
                          Tenant
                        </label>
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${!formData.tenant_pays_water ? 'bg-[#404040] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="water"
                            checked={!formData.tenant_pays_water}
                            onChange={() => setFormData({...formData, tenant_pays_water: false})}
                            className="hidden"
                          />
                          Landlord
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                      <span className="text-sm font-medium">Council Tax</span>
                      <div className="flex gap-2">
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${formData.tenant_pays_council_tax ? 'bg-[#FF8C42] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="council_tax"
                            checked={formData.tenant_pays_council_tax}
                            onChange={() => setFormData({...formData, tenant_pays_council_tax: true})}
                            className="hidden"
                          />
                          Tenant
                        </label>
                        <label className={`px-3 py-1 rounded cursor-pointer text-sm ${!formData.tenant_pays_council_tax ? 'bg-[#404040] text-white' : 'bg-gray-200'}`}>
                          <input
                            type="radio"
                            name="council_tax"
                            checked={!formData.tenant_pays_council_tax}
                            onChange={() => setFormData({...formData, tenant_pays_council_tax: false})}
                            className="hidden"
                          />
                          Landlord
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {formData.monthly_rent && (
                  <div className="p-4 bg-[#FF8C42]/10 rounded-lg">
                    <h3 className="font-semibold text-[#404040] mb-2">Summary</h3>
                    <div className="text-sm space-y-1">
                      <p>Monthly Rent: <span className="font-semibold">£{parseFloat(formData.monthly_rent).toLocaleString()}</span></p>
                      <p>Annual Rent: <span className="font-semibold">£{(parseFloat(formData.monthly_rent) * 12).toLocaleString()}</span></p>
                      <p>Deposit: <span className="font-semibold">£{parseFloat(formData.deposit_amount || 0).toLocaleString()}</span></p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review & Additional Terms */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">Review & Submit</h2>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-[#FF8C42] mb-2">Landlord</h3>
                    <p className="text-sm">{formData.landlord_name}</p>
                    <p className="text-sm text-gray-600">{formData.landlord_email}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-[#404040] mb-2">Tenant</h3>
                    <p className="text-sm">{formData.tenant_name}</p>
                    <p className="text-sm text-gray-600">{formData.tenant_email}</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-[#404040] mb-2">Property</h3>
                  <p className="text-sm">{formData.property_address}, {formData.property_postcode}</p>
                  <p className="text-sm text-gray-600">{formData.bedrooms} bedroom {formData.property_type}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-[#404040] mb-2">Financial</h3>
                  <p className="text-sm">Rent: <span className="font-semibold">£{parseFloat(formData.monthly_rent || 0).toLocaleString()}/month</span></p>
                  <p className="text-sm">Deposit: <span className="font-semibold">£{parseFloat(formData.deposit_amount || 0).toLocaleString()}</span></p>
                  <p className="text-sm">Dates: {formData.tenancy_start_date} to {formData.tenancy_end_date}</p>
                </div>

                {/* Utility Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-[#404040] mb-2">Utility Responsibility</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p>Electricity: <span className={formData.tenant_pays_electricity ? 'text-[#FF8C42] font-semibold' : 'text-[#404040] font-semibold'}>{formData.tenant_pays_electricity ? 'Tenant' : 'Landlord'}</span></p>
                    <p>Gas: <span className={formData.tenant_pays_gas ? 'text-[#FF8C42] font-semibold' : 'text-[#404040] font-semibold'}>{formData.tenant_pays_gas ? 'Tenant' : 'Landlord'}</span></p>
                    <p>Water: <span className={formData.tenant_pays_water ? 'text-[#FF8C42] font-semibold' : 'text-[#404040] font-semibold'}>{formData.tenant_pays_water ? 'Tenant' : 'Landlord'}</span></p>
                    <p>Council Tax: <span className={formData.tenant_pays_council_tax ? 'text-[#FF8C42] font-semibold' : 'text-[#404040] font-semibold'}>{formData.tenant_pays_council_tax ? 'Tenant' : 'Landlord'}</span></p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_terms">Additional Terms (optional)</Label>
                  <Textarea
                    id="additional_terms"
                    value={formData.additional_terms}
                    onChange={(e) => setFormData({...formData, additional_terms: e.target.value})}
                    className="mt-1 min-h-[100px]"
                    placeholder="Any additional terms or conditions..."
                  />
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-semibold text-[#404040] mb-2">Agreement Includes:</h3>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Standard UK AST terms</li>
                    <li>Tenant obligations (private dwelling, max occupants, utilities, etc.)</li>
                    <li>Landlord statutory duties (Housing Act compliance)</li>
                    <li>Agent authority & Power of Attorney clause</li>
                    <li>Electronic signature validity clause</li>
                    <li>Signature blocks for all parties</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="px-6">
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Previous
                </Button>
              ) : (
                <div></div>
              )}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#404040] hover:bg-[#333] text-white px-6"
                >
                  Next
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Generating...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Generate & Send
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
